import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";

import type { PaginationResponse } from "gitpod-next-api/gitpod/v1/pagination_pb";

/**
 * Parsed pagination token info.
 * Token format: "NEXT_PAGE/TOTAL_PAGES/TOTAL_COUNT" (paginate2 format)
 */
export type PaginationTokenInfo = {
    nextPage: number;
    totalPages: number; // -1 if unknown
    totalCount: number; // -1 if unknown
};

/**
 * Parse a pagination token in paginate2 format.
 * Format: "NEXT_PAGE/TOTAL_PAGES/TOTAL_COUNT" (e.g., "2/10/100")
 * Use -1 for unknown values (e.g., "2/-1/-1" when totals unavailable).
 *
 * @param token The pagination token to parse
 * @returns Parsed token info, or null if token is empty/invalid
 */
export function parsePaginationToken(token: string | undefined | null): PaginationTokenInfo | null {
    if (!token) {
        return null;
    }

    const parts = token.split("/");
    if (parts.length === 3) {
        const nextPage = parseInt(parts[0], 10);
        const totalPages = parseInt(parts[1], 10);
        const totalCount = parseInt(parts[2], 10);

        if (!isNaN(nextPage) && !isNaN(totalPages) && !isNaN(totalCount)) {
            return { nextPage, totalPages, totalCount };
        }
    }

    // Legacy format: just page number - no total info available
    const legacyPage = parseInt(token, 10);
    if (!isNaN(legacyPage)) {
        return { nextPage: legacyPage, totalPages: -1, totalCount: -1 };
    }

    return null;
}

/**
 * Auto-pagination utility that handles pagination requests internally.
 * @param fn Function that accepts no parameters but returns a paginated response
 * @param pageSize Optional page size (defaults to API default, max 100)
 * @returns All aggregated results from paginated requests
 *
 * @example
 * // Example usage:
 * const allUsers = await autoPaginateRaw(
 *   (params) => api.userService.listUsers(params),
 *   { organizationId: "my-org-id" }
 * ).then((results) => results.flatMap((r) => r.users));
 */
export async function autoPaginateRaw<T extends { pagination?: PaginationResponse }, P>(
    fn: (params: P) => Promise<T>,
    baseParams: P,
    pageSize?: number,
) {
    const maxRequests = 100;
    const results: T[] = [];

    let token = "";
    for (let i = 0; i < maxRequests; i++) {
        const paramsWithPagination = {
            ...baseParams,
            pagination: new PaginationRequest({ token, ...(pageSize !== undefined && { pageSize }) }),
        };

        const result = await fn(paramsWithPagination);
        results.push(result);
        token = result.pagination?.nextToken || "";

        if (i === maxRequests - 1 && !!token) {
            console.error(`Cancelling pagination at ${maxRequests} requests.`);
        }
        if (!token) {
            break;
        }
    }

    return results;
}

/**
 * Specialized paginator for collections that automatically extracts and flattens items.
 * @param fn Function that accepts parameters and returns a paginated response
 * @param baseParams Base parameters to pass to the function (without pagination)
 * @param extractor Function to extract the collection from each response
 * @param pageSize Optional page size (defaults to API default, max 100)
 * @returns Flattened array of all items across all pages
 *
 * @example
 * // Example usage:
 * const allUsers = await autoPaginate(
 *   (params) => api.userService.listUsers(params),
 *   { organizationId: "my-org-id" },
 *   (response) => response.users
 * );
 */
export async function autoPaginate<T extends { pagination?: PaginationResponse }, P, R>(
    fn: (params: P) => Promise<T>,
    baseParams: P,
    extractor: (result: T) => R[],
    pageSize?: number,
): Promise<R[]> {
    const results = await autoPaginateRaw(fn, baseParams, pageSize);
    return results.flatMap(extractor);
}
